import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import billingService from '../services/billingService';
import { apiErrorMessage } from '../utils/apiError';
import { formatDateTime } from '../utils/datetime';
import ConfirmModal from './shared/ConfirmModal';
import { SaveCancel } from './BillingTab';
import TemplateAssistantChat from './TemplateAssistantChat';
import './AddCorporateProfilePage.css';
import './PaymentPorterPage.css';
import './BillingSchedule.css';
import './BillingTemplates.css';

// A PARTNER'S BILLING TEMPLATES (V663) — Partner Settings > Templates > Billing.
//
//   BillingTemplatesList   the list on the Billing sub-tab
//   BillingTemplatePage    one template, as its own page (STYLE.md: no modals): HTML on the left, a live preview on
//                          the right, the placeholders it may use below — /partner-settings/billing-templates/:id
//
// A template is HTML with Mustache placeholders ({{hotel_name}}, {{#services}} … {{/services}}), filled for one
// billing period of a hotel and printed to PDF from the hotel's Billing tab (ui-v2-billing-documents). The lambda
// sanitises it on save; the preview here is a sandboxed frame that runs nothing.

const LIST_PATH = '/partner-settings?tab=templates&sub=billing';

const usePartnerId = () => {
  const { solePartnerId } = useAuth();
  const { selectedPartner } = useAccount();
  return selectedPartner || solePartnerId;
};

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// SIGNATURES (V667) — images of a signature, each with a name, for the partner's billing documents. Added on the
// page itself (a name, a PNG or JPEG of at most 1 MB) — STYLE.md: no modal for a two-field form.
const MAX_SIGNATURE_BYTES = 1024 * 1024;
const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Could not read that file'));
  reader.readAsDataURL(file);
});

const SignaturesSection = ({ partnerId }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (!partnerId) return;
    let live = true;
    billingService.listSignatures(partnerId)
      .then(res => { if (live) setRows(res.signatures || []); })
      .catch(err => { if (live) setError(apiErrorMessage(err, 'Could not load the signatures')); });
    return () => { live = false; };
  }, [partnerId]);

  const cancel = () => { setAdding(false); setName(''); setFile(null); setError(null); };

  const save = async () => {
    setError(null);
    if (!file) { setError('Choose an image of the signature.'); return; }
    if (!['image/png', 'image/jpeg'].includes(file.type)) { setError('The image must be a PNG or a JPEG.'); return; }
    if (file.size > MAX_SIGNATURE_BYTES) { setError('The image must be at most 1 MB.'); return; }
    setSaving(true);
    try {
      const created = await billingService.createSignature(partnerId, { name: name.trim(), imageBase64: await readAsDataUrl(file) });
      setRows(list => [...(list || []), { ...created, createdAt: new Date().toISOString() }]
        .sort((a, b) => a.name.localeCompare(b.name)));
      cancel();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not add the signature'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (sig) => {
    setConfirmDelete(null);
    setError(null);
    try {
      await billingService.deleteSignature(partnerId, sig.id);
      setRows(list => list.filter(r => r.id !== sig.id));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete the signature'));
    }
  };

  return (
    <div className="btpl-list btpl-signatures">
      <div className="btpl-list-header">
        <div>
          <h3 className="btpl-list-title">Signatures</h3>
          <p className="billing-muted">Images of a signature, each with a name — a PNG or JPEG of at most 1 MB.</p>
        </div>
        {!adding && (
          <button type="button" className="pp-rules-add-btn" onClick={() => setAdding(true)}>
            <PlusIcon />
            Add signature
          </button>
        )}
      </div>

      {adding && (
        <div className="btpl-signature-form">
          <div className="acp-row-2">
            <div className="acp-field">
              <label className="acp-label" htmlFor="bsig-name">Name <span className="acp-required">*</span></label>
              <input id="bsig-name" className="acp-input" value={name} maxLength={200} placeholder="e.g. Jamie Phillips"
                onChange={e => setName(e.target.value)} />
            </div>
            <div className="acp-field">
              <label className="acp-label" htmlFor="bsig-file">Signature image <span className="acp-required">*</span></label>
              <input id="bsig-file" className="acp-input" type="file" accept="image/png,image/jpeg"
                onChange={e => setFile((e.target.files && e.target.files[0]) || null)} />
            </div>
          </div>
          <SaveCancel saving={saving} blocked={!name.trim() || !file} onCancel={cancel} onSave={save} />
        </div>
      )}

      {error && <div className="acp-error-banner">{error}</div>}
      {!rows ? (
        !error && <p className="billing-muted">Loading signatures…</p>
      ) : rows.length === 0 ? (
        <p className="billing-muted">No signatures yet.</p>
      ) : (
        <table className="bsch-table btpl-table">
          <thead><tr><th>Signature</th><th>Name</th><th>Added</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {rows.map(sig => (
              <tr key={sig.id}>
                <td><img className="btpl-signature-img" src={sig.imageDataUrl} alt={`Signature: ${sig.name}`} /></td>
                <td>{sig.name}</td>
                <td>{formatDateTime(sig.createdAt)}</td>
                <td className="btpl-signature-actions">
                  <button type="button" className="bsch-icon-btn billing-documents-delete" title="Delete this signature"
                    aria-label="Delete this signature" onClick={() => setConfirmDelete(sig)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                      <path d="M10 11v6M14 11v6"/>
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {confirmDelete && createPortal(
        <ConfirmModal
          title="Delete signature"
          message={`Delete the signature "${confirmDelete.name}"?`}
          confirmLabel="Delete"
          danger
          onConfirm={() => remove(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />,
        document.body,
      )}
    </div>
  );
};

export const BillingTemplatesList = ({ partnerId }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!partnerId) return;
    let live = true;
    setRows(null);
    setError(null);
    billingService.listTemplates(partnerId)
      .then(res => { if (live) setRows(res.templates || []); })
      .catch(err => {
        if (!live) return;
        setError(err.response && err.response.status === 404
          ? "Only the partner's admins can manage its billing templates."
          : apiErrorMessage(err, 'Could not load the billing templates'));
      });
    return () => { live = false; };
  }, [partnerId]);

  if (error) return <p className="billing-muted">{error}</p>;
  if (!rows) return <p className="billing-muted">Loading billing templates…</p>;

  return (
    <div className="btpl-sections">
    <div className="btpl-list">
      <div className="btpl-list-header">
        <div>
          <h3 className="btpl-list-title">Billing templates</h3>
          <p className="billing-muted">
            Documents such as an order form, filled in for a hotel's billing period and printed to PDF from its Billing tab.
          </p>
        </div>
        <button type="button" className="pp-rules-add-btn" onClick={() => navigate('/partner-settings/billing-templates/new')}>
          <PlusIcon />
          New template
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="billing-muted">No billing templates yet.</p>
      ) : (
        <table className="bsch-table btpl-table">
          <thead><tr><th>Name</th><th>Last changed</th></tr></thead>
          <tbody>
            {rows.map(t => (
              <tr key={t.id} className="btpl-row" tabIndex={0} title="Open this template"
                onClick={() => navigate(`/partner-settings/billing-templates/${t.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/partner-settings/billing-templates/${t.id}`); }}>
                <td>{t.name}</td>
                <td>{formatDateTime(t.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
    <SignaturesSection partnerId={partnerId} />
    </div>
  );
};

// THE ORDER FORM AS A STARTING POINT — the hconnect order form (Al Ain Rotana's, 2026) rebuilt as HTML with its
// values replaced by placeholders, so a partner starts from a working document rather than an empty box. Monthly
// services in the services table; one-off charges (V676) in their own table, shown only when the period has any
// (V678 made the same change to templates already started from this).
const ORDER_FORM_STARTER = `<style>
  h1 { font-size: 20pt; margin: 0 0 4pt; }
  h2 { font-size: 12pt; margin: 16pt 0 6pt; border-bottom: 1px solid #d1d5db; padding-bottom: 3pt; }
  .sub { color: #4b5563; margin: 0 0 12pt; }
  table.kv td { padding: 2pt 12pt 2pt 0; vertical-align: top; }
  table.kv td:first-child { color: #4b5563; width: 45mm; }
  table.lines { width: 100%; margin-top: 6pt; }
  table.lines th, table.lines td { border: 1px solid #d1d5db; padding: 4pt 6pt; text-align: left; }
  table.lines th { background: #f3f4f6; }
  table.lines td.num, table.lines th.num { text-align: right; }
  .small { font-size: 9pt; color: #4b5563; }
</style>
<h1>ORDER FORM</h1>
<p class="sub">hconnect Integration Platform Implementation and Service</p>

<h2>Customer Details</h2>
<table class="kv">
  <tr><td>Hotel Name:</td><td>{{hotel_name}}</td></tr>
  <tr><td>Billing Address:</td><td>{{billing_address}}</td></tr>
</table>

<h2>Contact Details</h2>
<table class="kv">
  <tr><td>Contact Name:</td><td>{{billing_contact}}</td></tr>
  <tr><td>Contact Email:</td><td>{{billing_email}}</td></tr>
</table>

<h2>Terms and Conditions</h2>
<table class="kv">
  <tr><td>Start Date:</td><td>{{start_date}}</td><td>Payment:</td><td>{{payment_method}}</td></tr>
  <tr><td>End Date:</td><td>{{end_date}}</td><td>Payment Term:</td><td>{{payment_term}}</td></tr>
  <tr><td>Frequency:</td><td>{{frequency}}</td><td></td><td></td></tr>
</table>

<h2>Services Being Ordered</h2>
<table class="lines">
  <tr><th>Services</th><th>Start Date</th><th>End Date</th><th class="num">Unit Price</th><th class="num">Total</th></tr>
  {{#monthly_services}}
  <tr><td>{{service}}</td><td>{{from}}</td><td>{{to}}</td><td class="num">{{unit_price}}</td><td class="num">{{amount}}</td></tr>
  {{/monthly_services}}
  <tr><th colspan="4">{{#one_off_services.length}}Monthly total{{/one_off_services.length}}{{^one_off_services.length}}Total{{/one_off_services.length}}</th><th class="num">{{monthly_total}}</th></tr>
  {{#one_off_services.length}}
</table>

<h2>One-off Charges</h2>
<table class="lines">
  <tr><th>Charge</th><th>Date</th><th class="num">Amount</th></tr>
  {{#one_off_services}}
  <tr><td>{{service}}</td><td>{{from}}</td><td class="num">{{amount}}</td></tr>
  {{/one_off_services}}
  <tr><th colspan="2">One-off total</th><th class="num">{{one_off_total}}</th></tr>
  <tr><th colspan="2">Total, monthly and one-off</th><th class="num">{{total}}</th></tr>
  {{/one_off_services.length}}
</table>
<p class="small">All prices are in {{currency}} per hotel. Hotels with central finance departments will be invoiced collectively.</p>

<h2>Subscription Renewal Dates</h2>
<p class="small">Invoices will be sent in advance. All bank transfer charges are to be borne by the customer.
Services payment made annually in advance.</p>
<p class="small"><strong>Late Payment and Suspension of Services.</strong> Failure to make payment by the invoice due date
will incur a late fee of USD $100 per month. hconnect reserves the right to suspend Services if payment is not received
within 15 days of the due date.</p>
<p class="small">The terms of the attached master services agreement hold authority over this order form.</p>

<table class="kv" style="margin-top: 18pt;">
  <tr><td>Hotel Name:</td><td>{{hotel_name}}</td></tr>
  <tr><td>Authorised Person:</td><td>{{authorised_person}}</td></tr>
  <tr><td>Title:</td><td>{{authorised_title}}</td></tr>
  <tr><td>Signature:</td><td>_________________________</td></tr>
  <tr><td>Date:</td><td></td></tr>
</table>
`;

// A placeholder as the template author types it.
const tagOf = (key) => `{{${key}}}`;

const BillingTemplatePage = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const partnerId = usePartnerId();
  const isNew = templateId === 'new';
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [saved, setSaved] = useState({ name: '', body: '' });
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const editor = useRef(null);

  useEffect(() => {
    if (!partnerId) return;
    let live = true;
    (async () => {
      try {
        const list = await billingService.listTemplates(partnerId);
        if (!live) return;
        setPlaceholders(list.placeholders || []);
        if (!isNew) {
          const t = await billingService.getTemplate(partnerId, templateId);
          if (!live) return;
          setName(t.name);
          setBody(t.body);
          setSaved({ name: t.name, body: t.body });
        }
      } catch (err) {
        if (live) setError(apiErrorMessage(err, 'Could not load the template'));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [partnerId, templateId, isNew]);

  // Put a placeholder where the cursor is in the HTML, as if typed there.
  const insert = (text) => {
    const el = editor.current;
    if (!el) { setBody(b => b + text); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + text + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + text.length; });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isNew) await billingService.createTemplate(partnerId, { name, body });
      else await billingService.updateTemplate(partnerId, templateId, { name, body });
      navigate(LIST_PATH);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the template'));
      setSaving(false);
    }
  };

  const remove = () => setConfirmModal({
    title: 'Delete billing template',
    message: `Delete "${saved.name}"? Documents already generated from it are kept.`,
    onConfirm: async () => {
      setConfirmModal(null);
      try {
        await billingService.deleteTemplate(partnerId, templateId);
        navigate(LIST_PATH);
      } catch (err) {
        setError(apiErrorMessage(err, 'Could not delete the template'));
      }
    },
  });

  const groups = placeholders.reduce((acc, p) => {
    (acc[p.group] = acc[p.group] || []).push(p);
    return acc;
  }, {});
  const missing = !name.trim() || !body.trim();

  return (
    <div className="acp-page">
      <button type="button" className="pp-back-btn" onClick={() => navigate(LIST_PATH)}>← Back to billing templates</button>
      <div className="acp-card">
        <div className="acp-card-header">
          <h1 className="acp-title">{isNew ? 'New billing template' : (saved.name || 'Billing template')}</h1>
          <p className="acp-subtitle">
            HTML with placeholders, filled in for one billing period of a hotel and printed to PDF from the hotel's Billing tab.
          </p>
        </div>
        <div className="acp-divider" />
        {loading ? (
          <div className="acp-body"><p className="billing-muted">Loading…</p></div>
        ) : (
          <div className="acp-body">
            <div className="acp-field">
              <label className="acp-label" htmlFor="btpl-name">Name <span className="acp-required">*</span></label>
              <input id="btpl-name" className="acp-input" value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. Order form" maxLength={200} />
            </div>

            <div className="btpl-editor">
              <div className="acp-field">
                <div className="btpl-editor-label">
                  <label className="acp-label" htmlFor="btpl-body">HTML <span className="acp-required">*</span></label>
                  {!body.trim() && (
                    <button type="button" className="pp-criteria-add-btn" onClick={() => setBody(ORDER_FORM_STARTER)}>
                      Start from the order form
                    </button>
                  )}
                </div>
                <textarea id="btpl-body" ref={editor} className="acp-input btpl-code" value={body}
                  onChange={e => setBody(e.target.value)} spellCheck={false} />
              </div>
              <div className="acp-field">
                <span className="acp-label">Preview</span>
                {/* sandbox="": the template runs nothing here, as nothing runs when it is printed. */}
                <iframe title="Template preview" className="btpl-preview" sandbox="" srcDoc={body} />
              </div>
            </div>

            {/* THE ASSISTANT — asks Claude for a change to the template as the editor holds it now; the whole updated
                template it returns goes straight into the editor (and the preview). Nothing is saved until Save. */}
            <TemplateAssistantChat
              target="full"
              title="Template Assistant"
              height={260}
              onInsert={(html) => setBody(html)}
              assist={(messages) => billingService.templateAssistant(partnerId, messages, { name, body })}
              intro={'Ask for a change — "add a VAT line under the total", "put the one-off charges before the monthly '
                + 'services", "make the headings navy". The updated template goes into the editor above; check the '
                + 'preview, then Save. Nothing is saved until you do.'}
              placeholder="Describe the change you want…"
            />

            <div className="acp-field">
              <span className="acp-label">Placeholders</span>
              <span className="acp-hint">Click one to put it where the cursor is in the HTML. Repeat a table row by
                putting it between {'{{#services}}'} and {'{{/services}}'}.</span>
              <div className="btpl-placeholders">
                {Object.entries(groups).map(([group, list]) => (
                  <div key={group} className="btpl-placeholder-group">
                    <div className="btpl-placeholder-group-title">{group}</div>
                    {list.map(p => (
                      <button key={p.key} type="button" className="btpl-placeholder" title={p.label}
                        onClick={() => insert(tagOf(p.key))}>
                        <code>{tagOf(p.key)}</code>
                        <span>{p.label}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {error && <div className="acp-error-banner">{error}</div>}
          </div>
        )}
        <div className="acp-footer btpl-footer">
          {!isNew && !loading && (
            <button type="button" className="pp-criteria-add-btn pp-criteria-add-btn-danger btpl-delete" onClick={remove} disabled={saving}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                <path d="M10 11v6M14 11v6"/>
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
              </svg>
              Delete
            </button>
          )}
          <SaveCancel saving={saving} blocked={missing || loading} onCancel={() => navigate(LIST_PATH)} onSave={save} />
        </div>
      </div>
      {confirmModal && createPortal(
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel="Delete"
          danger
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />,
        document.body,
      )}
    </div>
  );
};

export default BillingTemplatePage;
